// src/components/client/ParticipantsSection.js
//
// Pass 85 — the operator PARTICIPANTS surface inside the Client CRM. A participant is the coached human, DISTINCT
// from the commercial client. One client may have MANY participants (a family). Identity is created/linked ONLY by an
// EXPLICIT operator action — booking guest names are shown as CONTEXT and can PREFILL a create form, but they never
// become an identity automatically (no name/email matching). No commercial/CRM data is stored on a participant.
import React, { useMemo, useState, useCallback } from 'react';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { useClientParticipants } from '../../hooks/useClientParticipants';
import { createParticipantProfile, linkClientParticipant } from '../../utils/participantWriters';
import { canOperateOrganization } from '../../utils/platformAccess';

const C = { card: '#FFFFFF', card2: '#F5F4F0', border: 'rgba(0,0,0,0.08)', blue: '#2563EB', green: '#16A34A', amber: '#D97706', red: '#DC2626', t1: '#1C1B18', t2: '#57564F', t3: '#8A8980' };
const box = { border: `1px solid ${C.border}`, borderRadius: 10, padding: '9px 11px', fontSize: 14, width: '100%', boxSizing: 'border-box' };

// De-duplicated, trimmed guest-name candidates from the client's bookings — CONTEXT only.
export function guestNameCandidates(bookings = []) {
  const seen = new Set(); const out = [];
  for (const b of bookings) {
    const names = [b?.leadGuest?.fullName, b?.requesterName, ...((b?.additionalGuests || []).map((g) => g?.name || g?.fullName))];
    for (const n of names) { const s = String(n || '').trim(); if (s && !seen.has(s.toLowerCase())) { seen.add(s.toLowerCase()); out.push(s); } }
  }
  return out.slice(0, 12);
}

function ParticipantsSection({ organizationId = '', clientId = '', bookings = [] }) {
  const { currentUser, userData } = useAuth() || {};
  const canManage = !!(organizationId && userData && canOperateOrganization(userData, organizationId));
  const [reloadKey, setReloadKey] = useState(0);
  const { loading, error, participants } = useClientParticipants(db, organizationId, clientId, reloadKey);
  const [form, setForm] = useState({ displayName: '', primaryDiscipline: '' });
  const [linkId, setLinkId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const candidates = useMemo(() => guestNameCandidates(bookings), [bookings]);
  const ctx = useMemo(() => ({ userData, actorAuthUserId: currentUser?.uid || '' }), [userData, currentUser]);

  const create = useCallback(async (displayName, primaryDiscipline) => {
    if (busy || !canManage) return;
    const name = String(displayName || '').trim();
    if (!name) { setErr('A participant name is required.'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await createParticipantProfile(db, { organizationId, displayName: name, primaryDiscipline: String(primaryDiscipline || '').trim(), ...ctx });
      await linkClientParticipant(db, { organizationId, clientId, participantId: res.participantId, ...ctx });
      setForm({ displayName: '', primaryDiscipline: '' }); setMsg(`Participant "${name}" created and linked.`); setReloadKey((k) => k + 1);
    } catch (e) { setErr(e?.message || 'Could not create the participant.'); } finally { setBusy(false); }
  }, [busy, canManage, organizationId, clientId, ctx]);

  const linkExisting = useCallback(async () => {
    if (busy || !canManage) return;
    const pid = String(linkId || '').trim();
    if (!pid) { setErr('Enter a participant id to link.'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await linkClientParticipant(db, { organizationId, clientId, participantId: pid, ...ctx });
      setLinkId(''); setMsg('Participant linked to this client.'); setReloadKey((k) => k + 1);
    } catch (e) { setErr(e?.message || 'Could not link the participant.'); } finally { setBusy(false); }
  }, [busy, canManage, organizationId, clientId, linkId, ctx]);

  return (
    <section data-testid="participants-section" style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: C.t1, margin: '0 0 4px' }}>Participants</h2>
      <div style={{ fontSize: 13, color: C.t2, marginBottom: 12 }}>The people who receive coaching for this client. A participant is separate from the commercial client and keeps a durable coaching identity across lessons and seasons.</div>

      {error ? <div role="alert" data-testid="participants-error" style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>Could not load participants: {error}</div>
        : loading ? <div style={{ fontSize: 13, color: C.t3 }}>Loading participants…</div>
        : participants.length === 0 ? <div data-testid="participants-empty" style={{ fontSize: 13, color: C.t3, background: C.card2, borderRadius: 10, padding: '10px 12px' }}>No participants linked yet. Create one below — booking guest names are shown only as context.</div>
        : (
          <div style={{ display: 'grid', gap: 8 }}>
            {participants.map((p) => (
              <div key={p.participantId} data-testid={`participant-${p.participantId}`} style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontWeight: 700, color: C.t1 }}>{p.missing ? '(missing profile)' : (p.displayName || '(unnamed)')}</div>
                {p.primaryDiscipline && <span style={{ fontSize: 12, color: C.t2, background: C.card2, padding: '2px 8px', borderRadius: 20 }}>{p.primaryDiscipline}</span>}
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11.5, fontWeight: 700, color: p.authUserId ? C.green : C.t3 }} data-testid={`participant-auth-${p.participantId}`}>{p.authUserId ? '● App account linked' : '○ No app account'}</span>
              </div>
            ))}
          </div>
        )}

      {canManage && (
        <div style={{ marginTop: 14, background: C.card2, borderRadius: 12, padding: 14 }}>
          {candidates.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.t2, marginBottom: 6 }}>From this client's bookings (context only — click to prefill; never auto-linked):</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {candidates.map((n) => (
                  <button key={n} type="button" data-testid={`guest-candidate-${n}`} disabled={busy} onClick={() => setForm((f) => ({ ...f, displayName: n }))}
                    style={{ padding: '5px 10px', borderRadius: 20, border: `1px solid ${C.border}`, background: C.card, color: C.t2, fontSize: 12.5, cursor: 'pointer' }}>+ {n}</button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 180px' }}>
              <label htmlFor="pf-name" style={{ fontSize: 12, fontWeight: 700, color: C.t2 }}>New participant name</label>
              <input id="pf-name" data-testid="participant-name" value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} placeholder="e.g. Alex (child)" style={box} />
            </div>
            <div style={{ flex: '0 1 140px' }}>
              <label htmlFor="pf-disc" style={{ fontSize: 12, fontWeight: 700, color: C.t2 }}>Sport (optional)</label>
              <input id="pf-disc" data-testid="participant-discipline" value={form.primaryDiscipline} onChange={(e) => setForm((f) => ({ ...f, primaryDiscipline: e.target.value }))} placeholder="ski / snowboard" style={box} />
            </div>
            <button type="button" data-testid="participant-create" disabled={busy || !form.displayName.trim()} onClick={() => create(form.displayName, form.primaryDiscipline)}
              style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: (busy || !form.displayName.trim()) ? '#93B4F5' : C.blue, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Create participant</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="pf-link" style={{ fontSize: 12, fontWeight: 700, color: C.t2 }}>Link an existing participant id</label>
              <input id="pf-link" data-testid="participant-link-id" value={linkId} onChange={(e) => setLinkId(e.target.value)} placeholder="participant_…" style={box} />
            </div>
            <button type="button" data-testid="participant-link" disabled={busy || !linkId.trim()} onClick={linkExisting}
              style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${C.blue}`, background: C.card, color: C.blue, fontWeight: 700, cursor: 'pointer' }}>Link</button>
          </div>
          {msg ? <div data-testid="participant-msg" style={{ color: C.green, fontSize: 12.5, marginTop: 8 }}>{msg}</div> : null}
          {err ? <div role="alert" data-testid="participant-form-error" style={{ color: C.red, fontSize: 12.5, marginTop: 8 }}>{err}</div> : null}
        </div>
      )}
    </section>
  );
}

export default ParticipantsSection;
